"use client";

import type { BlogPostListEntry } from "@/content/blog/types";
import { getLocaleFromPathname, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface FloatingPostNavigationProps {
  readonly posts: BlogPostListEntry[];
  readonly currentSlug: string | null;
  readonly currentTitle?: string;
}

interface NavigationTarget {
  readonly href: string;
  readonly title: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly icon: "previous" | "next";
}

const buttonBaseClasses =
  "group relative flex h-12 w-12 items-center justify-center gap-2 rounded-full border border-border/80 bg-card/95 text-neutral-emphasis shadow-lg ring-1 ring-ring/10 backdrop-blur transition duration-200 hover:-translate-y-0.5 hover:bg-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-emphasis sm:h-11 sm:w-auto sm:rounded-full sm:px-4";

function NavigationButton({ target }: { readonly target: NavigationTarget }) {
  const isNext = target.icon === "next";
  const Icon = isNext ? ArrowRight : ArrowLeft;

  return (
    <Link href={target.href} prefetch={false} className={buttonBaseClasses} aria-label={target.ariaLabel}>
      <span className="flex size-9 items-center justify-center rounded-full bg-neutral-emphasis text-primary-contrast shadow-md transition group-hover:bg-neutral-emphasis/90 group-focus-visible:bg-neutral-emphasis/90">
        <Icon className="size-4" />
      </span>
      <span className="hidden whitespace-nowrap text-sm font-semibold md:inline">
        {target.icon === "next" ? "Next post" : "Previous post"}
      </span>
    </Link>
  );
}

function MobileNavigationButton({
  target,
}: {
  readonly target: NavigationTarget;
}) {
  const isNext = target.icon === "next";
  const Icon = isNext ? ArrowRight : ArrowLeft;

  return (
    <Link
      href={target.href}
      prefetch={false}
      className="flex min-h-20 items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-neutral-emphasis shadow-sm transition hover:border-primary/40 hover:bg-neutral-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-emphasis"
      aria-label={target.ariaLabel}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-neutral-emphasis text-primary-contrast">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold uppercase tracking-wide text-neutral-muted">
          {target.label}
        </span>
        <span className="mt-1 block text-sm font-semibold leading-snug">
          {target.title}
        </span>
      </span>
    </Link>
  );
}

function createNavigationTarget(
  post: BlogPostListEntry,
  type: "previous" | "next",
  locale: Locale,
): NavigationTarget {
  const label = type === "previous" ? "Previous post" : "Next post";

  return {
    href: getLocalizedPathname(`/blog/${post.slug}/`, locale),
    title: post.title,
    label,
    ariaLabel: `${label}: ${post.title}`,
    icon: type,
  };
}

function getCurrentIndex(
  posts: BlogPostListEntry[],
  currentSlug: string | null,
  currentTitle?: string,
): number {
  if (currentSlug) {
    return posts.findIndex((post) => post.slug === currentSlug);
  }

  if (currentTitle) {
    return posts.findIndex((post) => post.title === currentTitle);
  }

  return -1;
}

export function FloatingPostNavigation({
  posts,
  currentSlug,
  currentTitle,
}: FloatingPostNavigationProps) {
  const pathname = usePathname();
  const currentLocale = getLocaleFromPathname(pathname);
  const currentIndex = getCurrentIndex(posts, currentSlug, currentTitle);

  if (currentIndex === -1) {
    return null;
  }

  const previousPost = currentIndex > 0 ? posts[currentIndex - 1] : null;
  const nextPost = currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null;

  const previousTarget = previousPost
    ? createNavigationTarget(previousPost, "previous", currentLocale)
    : null;
  const nextTarget = nextPost
    ? createNavigationTarget(nextPost, "next", currentLocale)
    : null;

  if (!previousTarget && !nextTarget) {
    return null;
  }

  return (
    <>
      <nav
        aria-label="Blog post navigation"
        className="mx-auto max-w-3xl px-4 pb-12 md:hidden"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {previousTarget ? (
            <MobileNavigationButton target={previousTarget} />
          ) : null}
          {nextTarget ? <MobileNavigationButton target={nextTarget} /> : null}
        </div>
      </nav>
      <nav
        aria-label="Blog post navigation"
        className="pointer-events-none fixed bottom-48 right-8 z-40 hidden md:block"
      >
        <div className="pointer-events-auto flex items-center justify-end gap-3">
          {previousTarget ? <NavigationButton target={previousTarget} /> : null}
          {nextTarget ? <NavigationButton target={nextTarget} /> : null}
        </div>
      </nav>
    </>
  );
}
